import { Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { finalize, switchMap, share, delay, catchError } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { HelperService, environment } from "@mypxplat/xplat/core";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private inFlightRequests = new Map<string, Observable<HttpEvent<any>>>();

  constructor(
    private authService: AuthService,
    private helperService: HelperService
  ) {}

  private useIdTokenForRequest(request: HttpRequest<any>): HttpRequest<any> {
    if (request.url.startsWith(environment.apiUrl) && this.helperService.idToken) {
      return request.clone({ setHeaders: { "X-USER-TOKEN": this.helperService.idToken } });
    }
    return request;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = this.useIdTokenForRequest(request);

    const requestIdentifier = this.getRequestIdentifier(request);
    if (this.inFlightRequests.has(requestIdentifier)) {
      return this.inFlightRequests.get(requestIdentifier);
    }

    let handledRequest: Observable<HttpEvent<any>>;

    if (request.headers.get("X-USER-TOKEN")) {
      var unixTimeStamp = Math.floor(new Date().getTime() / 1000);
      let isLegacyToken = request.headers.get("X-USER-TOKEN").length < 50;
      // sign-out-cognito-user is the first step of redirectToConnect's logout. When that logout is
      // itself handling a failed refresh, refreshing again deadlocks it: the refresh fails, the
      // in-progress handleAuthError is a no-op, and the redirect waits on this request forever.
      // Any other sign-out still refreshes first, so the global sign-out gets a valid token.
      let isAuthErrorSignOut = this.authService.handlingAuthError && request.url.includes("sign-out-cognito-user");
      let isLoggingOut = request.url.includes("logout") || isAuthErrorSignOut;
      if (isLegacyToken || isLoggingOut || (this.helperService.tokenExpiry && this.helperService.tokenExpiry > unixTimeStamp)) {
        handledRequest = next.handle(request);
      } else {
        handledRequest = this.authService.authenticate(request).pipe(
          switchMap((authenticatedRequest: any) => {
            return next.handle(authenticatedRequest);
          })
        );
      }
    } else {
      // Not checking auth because no token was passed to this request.
      handledRequest = next.handle(request);
    }

    // Using share() to multicast the observable.
    // this code means that if component A and component B both trigger a request to the same endpoint,
    // the request will only be sent once, and the response will be shared between the two components.
    const sharedRequestObservable = handledRequest.pipe(
      share(),
      delay(0),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Handle authentication error by redirecting to logout
          this.authService.handleAuthError(true);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.inFlightRequests.delete(requestIdentifier);
      })
    );
    this.inFlightRequests.set(requestIdentifier, sharedRequestObservable);

    return sharedRequestObservable;
  }

  private getRequestIdentifier(req: HttpRequest<any>): string {
    return `${req.method} ${req.url}${req.body ? " " + JSON.stringify(req.body) : ""}`;
  }
}
